
import Analytics from '@/helpers/analytics';
import Vue from 'vue';
import { CourseModel } from '@descomplica/dex-models';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { Icon, ButtonDropdown, Button, Tooltip, Loading } from '@descomplica/vue-components';
import { StudyPlanningModel } from '@/models/studyPlanning/StudyPlanningModel';
import { formatDate, getDiffTime } from '@/helpers/date';
import { getCourseCategory } from '@/helpers/category';
import { ContractExtensionEligibilityInfo } from '@/models/contractExtension/ContractExtensionEligibilityModel';
import ContractExtensionRequestService from '@/helpers/postgradContractExtension/request';
import { getChimeraToken } from '@/helpers/get-tokens';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import {
  formatExtensionOptionsWithPrice,
  ContractExtensionOptionWithPrice,
} from '@/helpers/postgradContractExtension/formatOptions';
import {
  DefaultContractExtensionTrackingProps,
  TrackPostgradContractExtensionButtonVisible,
  TrackTrialInteraction,
} from '@/helpers/analytics/types';
import { PostgradTrial, PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';

interface Data {
  showInformationTooltip: boolean;
  extensionRequestLoading: boolean;
}

export default Vue.extend({
  components: {
    Icon,
    ButtonDropdown,
    Button,
    Tooltip,
    Loading,
  },
  data(): Data {
    return {
      showInformationTooltip: false,
      extensionRequestLoading: false,
    };
  },
  computed: {
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    studyPlanning(): StudyPlanningModel {
      return this.$store.getters.studyPlanning(this.currentCourse.id);
    },
    expirationDateFormatted(): string {
      return formatDate(this.studyPlanning.expirationDate, 'DD / MM / YYYY');
    },
    contractExpirationDateToConsider(): Date {
      return (
        this.currentChimeraCourse?.contractByUser?.expirationDate ??
        this.studyPlanning.contractExpirationDate
      );
    },
    contractExpirationDateFormatted(): string {
      return formatDate(this.contractExpirationDateToConsider, 'DD / MM / YYYY');
    },
    courseEligibleForExtensionInformation(): ContractExtensionEligibilityInfo {
      return this.$store.getters.contractExtensionEligibilityInformationByCourseId(
        this.currentCourse.id,
      );
    },
    daysToFinishContract(): number {
      const todayDate = new Date();
      return getDiffTime(this.contractExpirationDateToConsider, todayDate).days;
    },
    trial(): PostgradTrial {
      return this.$store.getters.trialByDescomplicaCourseId(this.currentChimeraCourse?._id);
    },
    isUserInTrialCourse(): boolean {
      return !!this.trial;
    },
    isCourseEligibleForExtension(): boolean {
      return (
        !!this.courseEligibleForExtensionInformation &&
        !!this.courseEligibleForExtensionInformation?.extensionOptions.length &&
        !this.courseEligibleForExtensionInformation.isExpired &&
        !this.isUserInTrialCourse
      );
    },
    formattedExtensionOptions(): ContractExtensionOptionWithPrice[] {
      return formatExtensionOptionsWithPrice(this.courseEligibleForExtensionInformation);
    },
    trialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    defaultTrialTrackingProps(): TrackTrialInteraction {
      return {
        postgrad_trial: true,
        course: this.currentCourse.name,
        days_remaining: this.trialStatusByDescomplicaCourseId.days_remaining!,
        checkout_url: this.trial.originalCourseCheckoutUrl,
      };
    },
    defaultContractExtensionTrackingProps(): DefaultContractExtensionTrackingProps {
      return this.$store.getters.defaultContractExtensionTrackingProps;
    },
  },
  watch: {
    isCourseEligibleForExtension: {
      handler(value: boolean) {
        if (value) {
          this.trackContractExtensionButtonIsVisible();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async openExtensionCheckoutPage(option: ContractExtensionOptionWithPrice): Promise<void> {
      if (this.extensionRequestLoading) return;

      this.extensionRequestLoading = true;
      this.trackContractExtensionButtonClick(option);
      const requestResponse = await this.registerContractExtensionRequest(option.period);

      if (requestResponse) {
        const urlToOpen = option.url;

        this.trackRequestContractExtensionSuccess(option);
        window.open(urlToOpen, '_self');
      }

      this.extensionRequestLoading = false;
    },
    redirectToTrialOriginalCourseCheckout(): void {
      const { originalCourseCheckoutUrl } = this.trial;

      this.trackTrialOriginalCourseCheckoutRedirectionLinkClick();

      window.open(originalCourseCheckoutUrl, '_blank');
    },
    toggleIconTooltip(): void {
      this.showInformationTooltip = !this.showInformationTooltip;
    },
    trackContractExtensionButtonClick(option: ContractExtensionOptionWithPrice): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        in_postgrad_extension_course: true,
        days_to_finish_contract: this.daysToFinishContract,
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
        chosen_extension_option: option.period,
        ...this.defaultContractExtensionTrackingProps,
      });
    },
    trackRequestContractExtensionSuccess(option: ContractExtensionOptionWithPrice): void {
      Analytics.trackEvent(EVENT_LABELS.postgradRequestContractExtensionSuccessful, {
        days_to_finish_contract: this.daysToFinishContract,
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
        chosen_extension_option: option.period,
        ...this.defaultContractExtensionTrackingProps,
      });
    },
    trackContractExtensionButtonIsVisible(): void {
      Analytics.trackEvent<TrackPostgradContractExtensionButtonVisible>(
        EVENT_LABELS.postgradContractExtensionButtonVisible,
        {
          course: this.currentCourse.name,
          category: getCourseCategory(this.currentCourse) as string,
          ...this.defaultContractExtensionTrackingProps,
        },
      );
    },
    trackTrialOriginalCourseCheckoutRedirectionLinkClick(): void {
      Analytics.trackEvent<TrackTrialInteraction>(
        EVENT_LABELS.buttonClicked,
        this.defaultTrialTrackingProps,
      );
    },
    async registerContractExtensionRequest(period: number): Promise<boolean> {
      if (!this.currentChimeraCourse?.contractByUser) return false;

      const hasRequestBeenRegistered =
        await ContractExtensionRequestService.registerContractExtensionRequest({
          course: this.currentCourse,
          chimeraContractId: +this.currentChimeraCourse.contractByUser.id,
          chimeraToken: getChimeraToken(),
          period,
        });

      return hasRequestBeenRegistered;
    },
  },
});
